/**
 * analysisTargets — 문제 분석 v2 에서 「이번에 무엇을 만들지」를 정하는 규칙(화면과 떨어진 순수 함수).
 *
 * 2026-09-27 BE d3333ca 로 요청 options 각 키의 뜻이 셋이 됐다:
 *   false = 만들지 않음 · 생략 = 은행에 없을 때만 만듦 · **true = 있어도 다시 만듦**.
 * 생략은 「있는 데이터라서」 서버가 조용히 건너뛰는 길이라, 오너 결정(2026-09-27)으로 화면은 **넷을 늘
 * true/false 로 명시해** 보낸다. 무엇을 만들지는 창에서 눈에 보이는 체크가 정한다.
 *
 * 기본 체크(오너 지시 2026-09-27): **비어 있는 칸만 켠다. 넷 다 있으면 넷 다 켠다.**
 * 네 칸이 같은 기준이다 — 처음(D1 ①)에는 개념·난이도만 늘 켰는데(종전 생략 동작 그대로), 기준이 둘로
 * 갈려 헷갈린다는 지적으로 하나로 모았다. 다 채워진 문제에서 [AI 분석]을 여는 것은 있는 것을 다시
 * 받으려는 것이라 전부 켜 둔다. 승인 전에는 문제은행이 바뀌지 않는다.
 *
 * ⚠ 운영 옛 서버는 true 를 무시한다(생략과 같게 처리). 이 규칙을 쓰는 화면의 운영 푸시는 BE 운영 배포 뒤.
 */

export const ANALYSIS_TARGET_KEYS = ['concept_ids', 'difficulty', 'answer', 'solution'];

export const ANALYSIS_TARGET_LABEL = {
  concept_ids: '개념',
  difficulty: '난이도',
  answer: '정답',
  solution: '풀이',
};

const filled = (v) => v != null && String(v).trim() !== '';

/**
 * 분석 API 의 은행 현재값(`current`) → 칸별로 은행에 값이 있는가.
 * current 가 없으면 null(= 모른다) — 모르는 것을 「비어 있음」으로 말하지 않는다.
 *
 * has_solution 은 BE dev 12d5567(회신 BE-20260927-reply-fe-analysis-regenerate.md R1)부터 내용 기준이다 —
 * 내용 없는 풀이 저장은 「풀이 비우기」(행 삭제)가 됐다. 그 전 서버(운영)는 지운 풀이도 「있음」으로 줘서
 * 체크가 꺼진 채 열릴 수 있다 — 사람이 켜면 된다. 여기서 따로 고치지 않는다.
 */
export function presenceFromCurrent(current) {
  if (!current || typeof current !== 'object') return null;
  return {
    concept_ids: Array.isArray(current.concept_ids) && current.concept_ids.length > 0,
    difficulty: current.difficulty != null,
    answer: filled(current.answer),
    solution: !!current.has_solution,
  };
}

/**
 * 검색 목록 행의 need_* → 칸별로 은행에 값이 있는가. 플래그가 없는 칸은 null(= 모른다).
 */
export function presenceFromNeeds(item) {
  if (!item || typeof item !== 'object') return null;
  const has = (flag) => (typeof item[flag] === 'boolean' ? !item[flag] : null);
  return {
    concept_ids: has('need_concept'),
    difficulty: has('need_difficulty'),
    answer: has('need_answer'),
    solution: has('need_solution'),
  };
}

/**
 * 창을 열 때의 체크 — **비어 있다고 확인된 칸만** 켠다. 빈 칸이 하나도 없으면 넷 다 켠다.
 * 모르는 칸(null)은 빈 칸으로 세지 않는다 — 있는 값을 모른 채 「비었다」고 켜지 않는다.
 * 그래서 전부 모르면(은행 현재값을 못 받음) 「빈 칸 없음」과 같아 넷 다 켜진다. 실제로는 분석 현황·검색 응답이
 * 늘 이 값을 실어 와서 드문 경우다.
 */
export function defaultAnalysisTargets(presence) {
  const missing = {};
  ANALYSIS_TARGET_KEYS.forEach((k) => { missing[k] = presence?.[k] === false; });
  if (ANALYSIS_TARGET_KEYS.some((k) => missing[k])) return missing;
  const all = {};
  ANALYSIS_TARGET_KEYS.forEach((k) => { all[k] = true; });
  return all;
}

/** 요청 options — 넷을 늘 true/false 로 싣는다(생략 없음). */
export function toAnalysisOptions(targets) {
  const out = {};
  ANALYSIS_TARGET_KEYS.forEach((k) => { out[k] = !!targets?.[k]; });
  return out;
}

/** 하나라도 골랐는가 — 넷 다 끄면 서버가 400(「최소 한 필드는 켜져 있어야」)이라 창이 먼저 막는다. */
export function hasAnyTarget(targets) {
  return ANALYSIS_TARGET_KEYS.some((k) => !!targets?.[k]);
}

/**
 * 분석 결과(values 또는 result)에 이 칸의 값이 있는가 — 서버 승인의 「유효값」 판정과 같은 자.
 * 한 곳만 다르다: 풀이 편집본(solution_lexical)을 서버는 내용까지 본다(빈 문단뿐이면 「승인할 풀이 없음」,
 * 12d5567). 이 카드는 풀이를 고치지 않아 편집본이 생기지 않으므로 여기서는 있음만 본다.
 */
export function resultHas(vals, key) {
  if (!vals) return false;
  if (key === 'concept_ids') return Array.isArray(vals.concept_ids) && vals.concept_ids.length > 0;
  if (key === 'difficulty') return vals.difficulty != null;
  if (key === 'answer') return filled(vals.answer);
  if (key === 'solution') {
    return !!vals.solution_lexical || (Array.isArray(vals.solution_blocks) && vals.solution_blocks.length > 0);
  }
  return false;
}

/**
 * 결과 칸이 비었을 때 그 이유(분석 상세 한 건 기준).
 *  - 'not_requested' : 이번 분석이 이 칸을 요청하지 않았다(options[key] === false)
 *  - 'kept_existing' : 은행에 이미 있어 서버가 만들지 않았다(result.skipped_existing — 생략으로 보낸 요청)
 *  - null            : 그 밖(요청했는데 AI 가 못 냈다 · 검수에서 비웠다 · 이유를 싣기 전의 옛 분석)
 * 값이 있는 칸이면 묻지 않는다 — 부르는 쪽이 먼저 값 유무를 본다.
 */
export function emptySlotReason(detail, key) {
  const opts = detail?.options;
  if (opts && typeof opts === 'object' && opts[key] === false) return 'not_requested';
  const skipped = detail?.result?.skipped_existing ?? detail?.values?.skipped_existing;
  if (Array.isArray(skipped) && skipped.includes(key)) return 'kept_existing';
  return null;
}

/**
 * 풀이가 닿은 답 ↔ 은행 정답(BE dev 12d5567, 회신 BE-20260927-reply-fe-analysis-regenerate.md R3).
 * 서버가 원본 결과(result)에 싣는다:
 *   solution_answer — 새 풀이가 도달한 답(풀이를 만들었을 때만)
 *   answer_match    — 은행 정답과 같은가(true/false/null). null = 비교 대상 없음(정답도 다시 만듦·풀이를 안 만듦)
 *                     또는 판정 불가(주관식 수식 동치 등). 객관식 「③」「3」「(3)」은 서버가 같게 본다.
 *
 * **원본 결과에 대한 판정**이라, 검수 수정본이 정답이나 풀이를 고쳤으면(result_edited.answer·solution_lexical)
 * 낡은 값일 수 있어 둘 다 내지 않는다(회신 권고). 이 값들을 싣기 전의 옛 분석은 null.
 * 화면은 문자열 비교를 따로 하지 않는다 — 표기 정규화는 서버 몫이고, 여기서 다시 재면 「③」과 「3」을 다르다고 한다.
 *
 * @returns {{ mismatch: boolean, solutionAnswer: string|null } | null}
 */
export function solutionAnswerCheck(detail) {
  const r = detail?.result;
  if (!r || typeof r !== 'object') return null;
  const edited = detail?.result_edited || {};
  if (edited.answer != null || edited.solution_lexical != null) return null;
  const solutionAnswer = filled(r.solution_answer) ? String(r.solution_answer).trim() : null;
  const mismatch = r.answer_match === false;
  if (solutionAnswer == null && !mismatch) return null;
  return { mismatch, solutionAnswer };
}

/**
 * 정답만 다시 만들고 은행에 있는 풀이는 그대로 두는 선택인가 — 권고 문구를 띄울 자리(회신 R4-1).
 * 서버는 정답을 다시 만들 때 기존 풀이를 AI 에 보여 주지 않는다(의도 — 의심스러운 풀이에 끌려가지 않게).
 * 그래서 새 정답과 남은 풀이가 어긋날 수 있다. 은행에 풀이가 있는지 모르면(null) 말하지 않는다.
 */
export function answerOnlyRegen(targets, presence) {
  return !!targets?.answer && !targets?.solution && presence?.solution === true;
}

/**
 * 승인하면 이 칸에 값이 실리는가 — 검수 칸의 **지금 입력**으로 판정한다(승인은 미저장 수정을 먼저 저장한다).
 *
 * 정답·난이도를 비워 저장하면 지워지는 것이 아니라 **AI 원본(result)으로 돌아간다**(PATCH null = 수정 철회).
 * 그래서 사람이 비운 칸(입력 '' 이 처음 값과 다름)은 원본에 값이 있으면 실린다.
 * 개념은 빈 배열이 그대로 저장되므로 입력이 비면 안 실린다. 풀이는 이 카드에서 고치지 않아 결과 그대로다.
 *
 * @param edit   { conceptIds: number[], difficulty: string, answer: string } — 지금 입력
 * @param seeded { difficulty: string, answer: string } — 이 결과를 처음 채운 값
 * @param detail 분석 상세 — values(정본)·result(원본)
 */
export function willApply(key, edit, seeded, detail) {
  if (key === 'concept_ids') return Array.isArray(edit?.conceptIds) && edit.conceptIds.length > 0;
  if (key === 'solution') return resultHas(detail?.values, 'solution');
  const now = edit?.[key] ?? '';
  if (String(now).trim() !== '') return true;
  if (now === (seeded?.[key] ?? '')) return false; // 처음부터 비어 있었다 — 정본도 비어 있다
  return resultHas(detail?.result, key); // 사람이 비웠다 — 저장하면 AI 원본으로 돌아간다
}
